import React, { useContext, useEffect } from "react";
import { Container, Button, Row, Col } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { DISPOSAL_PATHS } from "../../../../Routes/paths";
import { useDisposalService } from "../api/useDisposal";
import { formatDate } from "../../../../common/service/CommonService";
import SectionCard from "Components/Common/SectionCard";
import FullPageLoader from "Components/Common/FullPageLoader";

import DetailField from "../../../../Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";

const ViewDisposal: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Drug Destruction", link: DISPOSAL_PATHS.LIST },
      { text: "View Disposal Record" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const { SelectIdQuery } = useDisposalService();
  const selectQuery = SelectIdQuery(id!);

  const data = selectQuery.data?.data?.[0];

  if (selectQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Record Not Found</h4>
            <p className="text-muted">The disposal record you are looking for does not exist.</p>

          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-700 mb-1">Disposal Protocol Details</h3>
            <p className="text-muted mb-0">
              Auth ID: DSP-{data.disposalid ?? id}&nbsp;|&nbsp;
              Study: {data.studycode}&nbsp;|&nbsp;
              Product: {data.productname}
            </p>
          </div>

        </div>

        <div className="d-flex flex-column gap-0.5">
          {/* 1 — Study & Product Details */}
          <SectionCard
            icon="ri-shield-check-line"
            title="Study & Product Details"
            subtitle="Study protocol and drug classification."
          >
            <Row>
              <DetailField label="Study Code" value={data.studycode} columns={3} />
              <DetailField label="Sponsor Name" value={data.sponsorname} columns={3} />
              <DetailField label="IP Category" value={data.ipcategory} columns={3} />
              <DetailField label="Product Name" value={data.productname} columns={3} />
              <DetailField label="Batch / Lot No" value={data.batchlotno} columns={3} />
              <DetailField label="Dosage Form" value={data.dosageform} columns={3} />
              <DetailField label="Strength" value={data.strength} columns={3} />
              <DetailField label="Expiry Date" value={formatDate(data.expirydate)} columns={3} />
            </Row>
          </SectionCard>

          {/* 2 — Disposal Drug Details */}
          <SectionCard
            icon="ri-medicine-bottle-line"
            title="Disposal Drug Details"
            subtitle="Quantities and rationale for disposal."
          >
            <Row>
              <DetailField label="Quantity Type" value={data.quantitytype} columns={3} />
              <DetailField label="Quantity Available For Disposal" value={data.totalquantity} columns={3} />
              <DetailField label="Quantity for Disposal" value={data.disposalquantity} columns={3} />
              <DetailField label="Disposal Reason" value={data.disposalreasontext} columns={3} />
              <DetailField label="Disposal Method" value={data.disposalmethodtext} columns={3} />
            </Row>
          </SectionCard>

          {/* 3 — Execution Details */}
          <SectionCard
            icon="ri-calendar-event-line"
            title="Execution Details"
            subtitle="Final disposal execution information."
          >
            <Row>
              <DetailField label="Disposal Date" value={formatDate(data.disposaldate)} columns={3} />
              <DetailField label="Disposal Location" value={data.disposallocationtext} columns={3} />
              <DetailField label="Disposal Vendor" value={data.disposalvendor} columns={3} />
            </Row>
          </SectionCard>
        </div>
      </Container>
    </div>
  );
};

export default ViewDisposal;
